import { useContext } from "react";
import { useForm } from "react-hook-form";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useSendEmail } from "../../hooks/useSendEmail";
// Components
import { RHFInputField } from "../../components/RHFInputField";
import { RHFSelectField } from "../../components/RHFSelectField";
import { RHFSelectFieldMultiple } from "../../components/RHFSelectFieldMultiple";
import { RHFInputFieldMultiline } from "../../components/RHFInputFieldMultiline";
// MUI
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";

export const WardrobeRequestFrom = ({ userCard, wardrobes }) => {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const {
    mutate,
    isSuccess,
    isError,
    error,
    reset: resetMutation,
  } = useSendEmail();

  let defaultValues = {
    userEmail: userCard.email,
    userName: userCard.name,
    requestType: "",
    wardrobeName: "",
    wardrobe: "",
    wardrobesMultiple: [],
    details: "",
  };

  // Form Validation text
  const validationHelper = {
    requestType: { required: "Type is required" },
    wardrobeName: { required: "Wardobe name is required" },
    wardrobe: { required: "Wardobe is required" },
    wardrobesMultiple: { required: "Wardobes is required" },
    details: { required: "This field is required" },
  };

  const { handleSubmit, control, watch, reset } = useForm({ defaultValues });

  const watchType = watch("requestType", false);

  const handleOnSubmit = (data) => {
    mutate(
      {
        replyEmail: userCard.email,
        replyName: userCard.name,
        subject: "Uniform Portal Wardrobe Request",
        message: JSON.stringify(data),
      },
      {
        onSuccess: () => {
          setAlertNotification({
            message: `Request Sent`,
            severity: "info",
          });
        },
      },
    );
  };

  const handleNewRequest = () => {
    reset();
    resetMutation();
  };

  const handleReset = () => {
    reset();
  };

  const requestTypeOptions = [
    { id: "new", label: "New wardrobe" },
    { id: "modify", label: "Change existing wardrobe" },
  ];

  const RequestType = () => (
    <RHFSelectField
      name="requestType"
      label="Request Type"
      options={requestTypeOptions}
      optionIdentifiers={{ id: "id", label: "label" }}
      control={control}
      validationHelper={validationHelper}
      rules={{ required: true }}
      viewMode={isSuccess}
    />
  );

  // If RequestType is "New" then ask this question
  const WardrobeName = () => {
    return (
      <RHFInputField
        name="wardrobeName"
        label="Wardrobe Name"
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={isSuccess}
      />
    );
  };

  const ExistingWardrobes = ({ required }) => (
    <RHFSelectField
      name="wardrobe"
      label="Wardrobe"
      options={wardrobes}
      optionIdentifiers={{ id: "id", label: "description" }}
      control={control}
      validationHelper={validationHelper}
      rules={{ required: required }}
      viewMode={isSuccess}
    />
  );

  const ExistingWardrobesMultiple = ({ required }) => {
    // Create options with description as the display value (since it already includes the ID)
    const wardrobeOptionsForDisplay = wardrobes.map((wardrobe) => ({
      ...wardrobe,
      displayLabel: wardrobe.description, // Use description as the display label since it includes ID
    }));

    return (
      <RHFSelectFieldMultiple
        name="wardrobesMultiple"
        label="Wardrobes"
        options={wardrobeOptionsForDisplay}
        optionIdentifiers={{ id: "id", label: "displayLabel" }}
        control={control}
        validationHelper={validationHelper}
        rules={{ required: required }}
        viewMode={isSuccess}
      />
    );
  };

  const Details = () => {
    return (
      <RHFInputFieldMultiline
        name="details"
        label="Request details"
        placeholder="Please enter as many details as possible. Include details such as product code, colour, and branding."
        rows={6}
        control={control}
        validationHelper={validationHelper}
        rules={{ required: true }}
        viewMode={isSuccess}
      />
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        maxWidth: 800,
      }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmit(handleOnSubmit)}
    >
      <Card>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ m: 2 }}>
            Wardrobe Request
          </Typography>
        </Box>
        <Divider />
        <Stack direction="column" spacing={2} sx={{ m: 2 }}>
          <RequestType />
          {watchType === "new" && (
            <>
              <WardrobeName />

              <Typography>
                If you would like to copy items from existing wardrobes, please
                select them below.
              </Typography>
              <ExistingWardrobesMultiple />
              <Details />
            </>
          )}
          {watchType === "modify" && (
            <>
              <Typography>
                Please select the wardrobe you would like to modify.
              </Typography>
              <ExistingWardrobes required />
              <Details />
            </>
          )}
        </Stack>
        <Divider />
        <CardActions
          sx={{
            alignSelf: "stretch",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 2,
          }}
        >
          {isSuccess ? (
            <>
              <Typography>Your request has been sent to our team.</Typography>
              <Button onClick={handleNewRequest} variant="outlined">
                New Request
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" onClick={handleReset}>
                Reset
              </Button>
              <Button type="submit" variant="contained">
                Send
              </Button>
            </>
          )}
          {isError && <Typography>Error: {JSON.stringify(error)}</Typography>}
        </CardActions>
      </Card>
    </Box>
  );
};
